/**
 * PATH: packages/ui/src/components/settings/team-tab/InviteModal.tsx
 * PURPOSE: Modal dialog for inviting a new team member
 *
 * WHY: Extracted from TeamTab.tsx to keep the orchestrator under 300 lines.
 *      All invite-specific state (email, firstName, lastName, role, inviting,
 *      error) lives inside this component — the parent only receives the
 *      completed invite payload.
 *
 * FLOW:
 *   ┌──────────────┐    ┌──────────────┐    ┌────────────────┐
 *   │ User fills   │───▶│ Validate +   │───▶│ onInvite(data) │
 *   │ form fields  │    │ submit       │    │ → parent saves │
 *   └──────────────┘    └──────────────┘    └────────────────┘
 *
 * DEPENDENCIES:
 *   - lucide-react: X icon
 *   - ../../../lib/utils: cn() class merging
 *   - ./team-tab-primitives: RoleOption type
 */

import { useState } from 'react'
import { X } from 'lucide-react'
import { cn } from '../../../lib/utils'
import type { RoleOption } from './team-tab-primitives'
import { groupRoleOptionsByCategory } from './team-tab-primitives'

export interface InvitePayload {
  email: string
  first_name: string
  last_name: string
  role: string
  /**
   * RBAC V2 (MR 11 / MR 12) — capability toggles. Orthogonal to
   * `role`. Each gates a separate piece of UI:
   *   - `has_finance_org_wide` → CFO module nav item
   *   - `is_org_admin_user`    → Settings → Team admin
   *   - `is_billing_admin`     → Settings → Subscription / billing
   * Owner-tier invites server-side force all three to true regardless
   * of what's sent here, so the UI auto-checks + disables them when
   * role becomes a synonym for "owner".
   */
  has_finance_org_wide?: boolean
  is_org_admin_user?: boolean
  is_billing_admin?: boolean
}

export interface InviteModalProps {
  roleOptions: RoleOption[]
  onInvite: (data: InvitePayload) => Promise<void>
  onClose: () => void
  className?: string
}

// Role codes that map to the V2 owner tier server-side (per
// derive_tier_from_hierarchy — hierarchy <= 15). When any of these
// is selected the 3 capability checkboxes auto-tick and disable so
// the operator doesn't have to remember the "owners get everything"
// rule. Kept as a frontend-only short-list rather than fetched from
// the backend because (a) it's a tiny stable set, (b) the canonical
// enforcement still happens server-side, (c) the FE is free to be
// slightly wrong: if an org defines a custom owner-tier
// SystemRole the backend will still force the toggles on, the
// checkboxes just won't auto-tick.
const OWNER_TIER_ROLE_CODES = new Set([
  'owner',
  'org_owner',
])

export function InviteModal({ roleOptions, onInvite, onClose, className }: InviteModalProps) {
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  // RBAC MR 9 — role is now MANDATORY with no preselected default. The
  // inviter must consciously pick a tier instead of getting "member"
  // silently. Two reasons: (a) a stray invite at `member` tier still
  // pollutes the OrganisationMember table even after MR 7 made it
  // a real (not hidden) tier; (b) the senior brief was emphatic that
  // every invite should be a considered decision about access scope.
  // Empty string here renders the placeholder option below as the
  // initial selection.
  const [role, setRole] = useState('')
  // RBAC V2 (MR 11 / MR 12) — capability toggles. Default OFF for
  // every role except owner (locked-on at submit time). Operators
  // who DO want a non-owner to have finance / admin / billing must
  // explicitly tick — same "considered decision" principle as the
  // mandatory role pick.
  const [hasFinanceOrgWide, setHasFinanceOrgWide] = useState(false)
  const [isOrgAdminUser, setIsOrgAdminUser] = useState(false)
  const [isBillingAdmin, setIsBillingAdmin] = useState(false)
  const [inviting, setInviting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isOwnerTier = OWNER_TIER_ROLE_CODES.has(role)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return
    // RBAC MR 9 — explicit role-pick guard. The submit button is also
    // disabled when role is empty, but a keyboard "Enter" on the email
    // field would still fire this handler — so guard here too. The
    // browser's native HTML5 `required` on the <select> works in
    // modern browsers but is silently degraded by some legacy custom-
    // select polyfills, so we don't rely on it alone.
    if (!role) {
      setError('Please pick a role for the new member.')
      return
    }

    setInviting(true)
    setError(null)

    try {
      await onInvite({
        email,
        first_name: firstName,
        last_name: lastName,
        role,
        // Owner-tier force-True at submit time mirrors the BE rule
        // (see MR 12 BE / organisation/views/team_members_views/
        // list_create_views.py). Even if the operator unchecked the
        // boxes for some reason, an owner invite always gets all
        // three capabilities — anything else creates a "phantom
        // owner" who can't see CFO / can't manage team / can't
        // change billing, which is never what the operator meant.
        has_finance_org_wide: isOwnerTier ? true : hasFinanceOrgWide,
        is_org_admin_user: isOwnerTier ? true : isOrgAdminUser,
        is_billing_admin: isOwnerTier ? true : isBillingAdmin,
      })
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to invite member'
      setError(errorMsg)
    } finally {
      setInviting(false)
    }
  }

  const inputCn = cn(
    "w-full px-3 py-2 rounded-lg border",
    "border-slate-200 dark:border-slate-700",
    "bg-white dark:bg-slate-800",
    "text-slate-900 dark:text-white",
    "focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
  )

  return (
    <div className={cn("fixed inset-0 z-50 flex items-center justify-center bg-black/50", className)}>
      <div className={cn(
        "w-full max-w-md mx-4 p-6 rounded-xl",
        "bg-white dark:bg-slate-900",
        "border border-slate-200 dark:border-slate-700 shadow-xl"
      )}>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-slate-900 dark:text-white">
            Invite team member
          </h4>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Email *
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputCn}
              placeholder="colleague@company.com"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                First name
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={inputCn}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Last name
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className={inputCn}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className={inputCn}
              required
              aria-invalid={!role}
            >
              {/*
                RBAC MR 9 - placeholder option forces an active pick.
                ``value=""`` + ``disabled`` keeps it visible as the
                initial state but unselectable as a final submission;
                ``required`` on the parent <select> + the submit-guard
                in handleSubmit() complete the mandatory-pick contract.
              */}
              <option value="" disabled>
                Select a role…
              </option>
              {groupRoleOptionsByCategory(roleOptions).map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/*
            RBAC V2 (MR 11 / MR 12) - capability toggles. Three
            orthogonal checkboxes, each gating a distinct piece of
            UI elsewhere in the app:
              ▢ CFO module (org-wide finance)
              ▢ Team admin (invite + role-change in settings)
              ▢ Billing admin (Quotech subscription)
            When the role is owner-tier the boxes auto-tick + disable
            because owners always have every capability - see the
            handleSubmit force-True branch above for the contract.
            We render the label as "data-testid"-friendly so the
            future test plan (operator guide §4) can target each
            toggle without a brittle CSS selector.
          */}
          <fieldset
            className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 space-y-2"
            disabled={isOwnerTier}
            aria-describedby="capability-toggles-help"
          >
            <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Capability toggles
            </legend>
            <p
              id="capability-toggles-help"
              className="text-xs text-slate-500 dark:text-slate-400"
            >
              {isOwnerTier
                ? 'Owners automatically have every capability - these are locked on.'
                : 'Tick to grant cross-cutting capability independent of role. Leave unticked for normal users.'}
            </p>
            <label
              className="flex items-start gap-2 text-sm cursor-pointer"
              data-testid="invite-toggle-finance"
            >
              <input
                type="checkbox"
                className="mt-0.5 rounded border-slate-300 dark:border-slate-600 text-teal-600 focus:ring-teal-500"
                checked={isOwnerTier ? true : hasFinanceOrgWide}
                onChange={(e) => setHasFinanceOrgWide(e.target.checked)}
              />
              <span>
                <span className="font-medium text-slate-900 dark:text-white">CFO access</span>
                <span className="block text-xs text-slate-500 dark:text-slate-400">
                  Unlocks the CFO module - org-wide finance, cashflow, payroll roll-up.
                </span>
              </span>
            </label>
            <label
              className="flex items-start gap-2 text-sm cursor-pointer"
              data-testid="invite-toggle-org-admin"
            >
              <input
                type="checkbox"
                className="mt-0.5 rounded border-slate-300 dark:border-slate-600 text-teal-600 focus:ring-teal-500"
                checked={isOwnerTier ? true : isOrgAdminUser}
                onChange={(e) => setIsOrgAdminUser(e.target.checked)}
              />
              <span>
                <span className="font-medium text-slate-900 dark:text-white">Team admin</span>
                <span className="block text-xs text-slate-500 dark:text-slate-400">
                  Can invite, disable, and change tiers/toggles for other members.
                </span>
              </span>
            </label>
            <label
              className="flex items-start gap-2 text-sm cursor-pointer"
              data-testid="invite-toggle-billing"
            >
              <input
                type="checkbox"
                className="mt-0.5 rounded border-slate-300 dark:border-slate-600 text-teal-600 focus:ring-teal-500"
                checked={isOwnerTier ? true : isBillingAdmin}
                onChange={(e) => setIsBillingAdmin(e.target.checked)}
              />
              <span>
                <span className="font-medium text-slate-900 dark:text-white">Billing admin</span>
                <span className="block text-xs text-slate-500 dark:text-slate-400">
                  Can view + change the Quotech subscription and payment method.
                </span>
              </span>
            </label>
          </fieldset>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              // RBAC MR 9 — also disabled until a role is picked.
              disabled={inviting || !email || !role}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium text-white",
                "bg-teal-600 hover:bg-teal-700",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
              title={!role ? 'Pick a role for the new member first.' : undefined}
            >
              {inviting ? 'Sending...' : 'Send invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
